/**
 * Versandrouting Stufe 1 (docs/storeos/12-versandrouting.md, R112–R118): reine Entscheidungsfunktion ohne DB-Zugriff.
 *
 * `waehleVersand(eingabe, tarife, regeln)` prüft jede Versandoption gegen die harten Filter (§ 2.2, R115), rechnet für die
 * zulässigen die Grenzkosten (§ 2.3, R114: Porto + Zuschläge + Präferenz + Abholgebühr-Anteil + Verzug; die Grundgebühr
 * geht nie in die Wahl ein) und liefert alle Kandidaten mit Preisbestandteilen oder Ausschlussgrund samt Klartext-Grund.
 * Die API speichert das Ergebnis als Entscheidungszeile (`versand_entscheidungen`, nur INSERT); die SPA kann dieselbe
 * Funktion für Anzeigen nutzen. Datumslogik rechnet auf Kalendertagen 'YYYY-MM-DD' und Uhrzeiten 'HH:MM' (Europe/Berlin,
 * vom Aufrufer bestimmt), damit die Funktion deterministisch und ohne Zeitzonenwissen testbar ist.
 *
 * Stufe 1 ändert nichts am Versandweg: Bei jedem Wert von VERSANDROUTING ist die Wahl nur ein Schattenprotokoll (R118).
 */
import { centsAlsText, eur, rundeCents } from './geld.js';

// ── Schalter ─────────────────────────────────────────────────────────────

export const VERSANDROUTING_MODI = ['aus', 'vorschlag', 'automatisch'] as const;
export type VersandroutingModus = (typeof VERSANDROUTING_MODI)[number];

export interface VersandroutingSchalter {
  /** Wert aus der Umgebung (ungültig → 'aus'). */
  konfiguriert: VersandroutingModus;
  /** Was tatsächlich gilt. In Stufe 1 immer 'aus': `vorschlag`/`automatisch` sind validiert, aber noch nicht umgesetzt. */
  wirksam: 'aus';
  /** Klartext für Log und Einstellungen, wenn konfiguriert ≠ wirksam oder der Wert ungültig war. */
  hinweis: string | null;
}

/** R118: `VERSANDROUTING=aus|vorschlag|automatisch`, Default `aus`. Groß-/Kleinschreibung egal, Leerraum wird ignoriert. */
export function versandroutingSchalter(roh: string | null | undefined): VersandroutingSchalter {
  const w = (roh ?? '').trim().toLowerCase();
  if (!w) return { konfiguriert: 'aus', wirksam: 'aus', hinweis: null };
  if (!(VERSANDROUTING_MODI as readonly string[]).includes(w)) {
    return { konfiguriert: 'aus', wirksam: 'aus', hinweis: `VERSANDROUTING="${roh}" ist ungültig (erlaubt: ${VERSANDROUTING_MODI.join(', ')}); es gilt "aus"` };
  }
  const m = w as VersandroutingModus;
  if (m === 'aus') return { konfiguriert: 'aus', wirksam: 'aus', hinweis: null };
  return {
    konfiguriert: m, wirksam: 'aus',
    hinweis: `VERSANDROUTING=${m} ist noch nicht umgesetzt (Stufe 1): StoreOS rechnet nur im Schattenbetrieb, Produktwahl, Label und Tagesabschluss bleiben wie heute`,
  };
}

// ── Empfänger und Ziel ───────────────────────────────────────────────────

export const EMPFAENGERTYPEN = ['geschaeft', 'privat', 'packstation_filiale'] as const;
export type Empfaengertyp = (typeof EMPFAENGERTYPEN)[number];
export type Zieltyp = 'adresse' | 'packstation' | 'postfiliale';

interface AdressMerkmale { firma?: string | null; drop_point?: { typ: string } | null }

/**
 * Empfängertyp aus den vorhandenen Adressdaten (Vorbereitung für spätere Kriterien, z. B. Geschäftskunden per UPS,
 * Privatkunden per DPD): Packstation/Postfiliale → `packstation_filiale`, sonst Firma gesetzt → `geschaeft`, sonst `privat`.
 * Die Abgabestelle hat Vorrang vor der Firma: Eine Packstation ist keine Geschäftsadresse mit Öffnungszeiten, und ein
 * Firmenname im Packstation-Datensatz würde sonst eine Geschäftszustellung vortäuschen.
 */
export function empfaengertypAus(a: AdressMerkmale | null | undefined): Empfaengertyp {
  if (a?.drop_point && (a.drop_point.typ === 'packstation' || a.drop_point.typ === 'postfiliale')) return 'packstation_filiale';
  if (a?.firma && a.firma.trim()) return 'geschaeft';
  return 'privat';
}

export function zieltypAus(a: AdressMerkmale | null | undefined): Zieltyp {
  if (a?.drop_point?.typ === 'packstation') return 'packstation';
  if (a?.drop_point?.typ === 'postfiliale') return 'postfiliale';
  return 'adresse';
}

/** Außenmaß L ≥ B ≥ H in mm aus dem Innenmaß der Verpackung plus Aufschlag (Default 10 mm, Konzept § 1.3). 0-Maße (Sonderverpackung) → unbekannt. */
export function aussenmass(innen: { l: number; b: number; h: number } | null | undefined, aufschlagMm = 10): { l: number; b: number; h: number } | null {
  if (!innen || !(innen.l > 0 && innen.b > 0 && innen.h > 0)) return null;
  const [l, b, h] = [innen.l + aufschlagMm, innen.b + aufschlagMm, innen.h + aufschlagMm].sort((x, y) => y - x);
  return { l, b, h };
}

// ── Tarifdaten (Spiegel der Tabellen aus Migration 500) ─────────────────

export interface Versandkonto {
  id: string; code: string; name: string; carrier: string; adapter: string;
  abholung: 'keine' | 'regel' | 'auf_abruf';
  /** ISO-Wochentage der Abholung (1 = Montag … 7 = Sonntag). */
  abholtage: number[];
  abholung_vorlauf_werktage: number;
  grundgebuehr_cents: number;
  gueltig_von: string; gueltig_bis: string | null;
  aktiv: boolean;
  quelle: string | null; unsicher: boolean; hinweis: string | null;
}

export interface Versandoption {
  id: string; konto_id: string; code: string; name: string; produkt: string;
  einlieferung: 'abholung' | 'filiale' | 'paketshop' | 'briefkasten' | 'grossannahme';
  annahmeschluss: string; abholung_von: string | null; abholung_bis: string | null;
  abholung_bestaetigt: boolean;
  abholgebuehr_tag_cents: number;
  tracking: boolean;
  haftung_cents: number | null;
  zieltypen: Zieltyp[];
  max_g: number | null; max_l_mm: number | null; max_b_mm: number | null; max_h_mm: number | null; max_lk_mm: number | null;
  rang: number; aktiv: boolean;
  quelle: string | null; unsicher: boolean; hinweis: string | null;
}

export interface Tarifklasse {
  id: string; option_id: string; rang: number; name: string; zone: string;
  max_g: number | null; max_l_mm: number | null; max_b_mm: number | null; max_h_mm: number | null; max_lk_mm: number | null;
  preis_cents: number;
  gueltig_von: string; gueltig_bis: string | null;
  quelle: string | null; unsicher: boolean;
}

export interface Zuschlag {
  id: string; konto_id: string; option_id: string | null; name: string;
  art: 'fix' | 'prozent'; betrag_cents: number | null; basispunkte: number | null;
  bedingung: 'immer' | 'zeitraum' | 'zieltyp_packstation' | 'zieltyp_postfiliale';
  gueltig_von: string; gueltig_bis: string | null;
  quelle: string | null; unsicher: boolean;
}

/**
 * Präferenz (vorbereitet, im Seed leer): je Option × Bedingung ein Auf-/Abschlag in Cent (negativ = Abschlag) oder ein
 * Ausschluss, mit Gültigkeit. Heute gibt es nur die Bedingung Empfängertyp; weitere Kriterien (Zustellquote je Region,
 * Kundenwunsch, Nachhaltigkeit) kommen als neue Bedingungsarten dazu, ohne die Kostenfunktion umzubauen.
 */
export const PRAEFERENZ_BEDINGUNGEN = ['empfaengertyp'] as const;
export interface Praeferenz {
  id: string; option_id: string;
  bedingung: (typeof PRAEFERENZ_BEDINGUNGEN)[number]; wert: string;
  wirkung: 'aufschlag' | 'ausschluss';
  betrag_cents: number | null;
  grund: string;
  gueltig_von: string; gueltig_bis: string | null;
  quelle: string | null;
}

export interface Tarifstand {
  konten: Versandkonto[];
  optionen: Versandoption[];
  klassen: Tarifklasse[];
  zuschlaege: Zuschlag[];
  praeferenzen: Praeferenz[];
}

// ── Regeln (Einstellungen, Admin) ────────────────────────────────────────

export const SPITZENTAG_VERHALTEN = ['vorschlagen', 'beauftragen'] as const;
export type SpitzentagVerhalten = (typeof SPITZENTAG_VERHALTEN)[number];

export interface Versandregeln {
  /** Vorgabe Thomas 18.10.2026: keine Kapazität für Selbsteinlieferung. Standard aus. */
  selbsteinlieferung_erlaubt: boolean;
  tracking_pflicht: boolean;
  /** Warenwert, bis zu dem StoreOS das Haftungsrisiko selbst trägt (über der Carrier-Haftung). 0 = keine Selbstversicherung. */
  selbstversicherung_bis_cents: number;
  /** Optionen, die mehr Werktage später abgeholt werden als die früheste, scheiden aus. */
  laufzeitverlust_max_werktage: number;
  /** Was ein Werktag späterer Versand kostet (Voreinstellung 50 Cent, Thomas hat noch nicht final entschieden). */
  verzug_cents_je_werktag: number;
  /** Zusatzabholung an Spitzentagen: nur vorschlagen (Voreinstellung) oder beauftragen (folgt mit der Pickup-API, bis dahin wie vorschlagen). */
  spitzentag_verhalten: SpitzentagVerhalten;
  /** Ab so vielen Paketen am Tag schlägt StoreOS eine Zusatzabholung vor (DHL-Bedarfsabholung ≤ 10, darüber Einmalabholung). */
  abholung_zusatz_ab_paketen: number;
  /** Erwarteter Packzeitpunkt für die vorläufige Wahl beim Import (Billbee-Zeitstempel: Spitze 8–10 Uhr). */
  erwartete_packzeit: string;
}

export const VERSANDREGELN_STANDARD: Versandregeln = {
  selbsteinlieferung_erlaubt: false,
  tracking_pflicht: true,
  selbstversicherung_bis_cents: 0,
  laufzeitverlust_max_werktage: 1,
  verzug_cents_je_werktag: 50,
  spitzentag_verhalten: 'vorschlagen',
  abholung_zusatz_ab_paketen: 40,
  erwartete_packzeit: '09:00',
};

const ZEIT = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Gespeicherte Regeln (JSON) mit Voreinstellungen auffüllen; unbrauchbare Einzelwerte fallen auf die Voreinstellung zurück. */
export function regelnAus(roh: unknown): Versandregeln {
  const r = (roh && typeof roh === 'object' ? roh : {}) as Record<string, unknown>;
  const s = VERSANDREGELN_STANDARD;
  const ganz = (v: unknown, std: number, min = 0, max = 1_000_000) => (Number.isInteger(v) && (v as number) >= min && (v as number) <= max ? (v as number) : std);
  const ja = (v: unknown, std: boolean) => (typeof v === 'boolean' ? v : std);
  return {
    selbsteinlieferung_erlaubt: ja(r.selbsteinlieferung_erlaubt, s.selbsteinlieferung_erlaubt),
    tracking_pflicht: ja(r.tracking_pflicht, s.tracking_pflicht),
    selbstversicherung_bis_cents: ganz(r.selbstversicherung_bis_cents, s.selbstversicherung_bis_cents),
    laufzeitverlust_max_werktage: ganz(r.laufzeitverlust_max_werktage, s.laufzeitverlust_max_werktage, 0, 10),
    verzug_cents_je_werktag: ganz(r.verzug_cents_je_werktag, s.verzug_cents_je_werktag, 0, 10_000),
    spitzentag_verhalten: (SPITZENTAG_VERHALTEN as readonly string[]).includes(String(r.spitzentag_verhalten)) ? (r.spitzentag_verhalten as SpitzentagVerhalten) : s.spitzentag_verhalten,
    abholung_zusatz_ab_paketen: ganz(r.abholung_zusatz_ab_paketen, s.abholung_zusatz_ab_paketen, 1, 10_000),
    erwartete_packzeit: typeof r.erwartete_packzeit === 'string' && ZEIT.test(r.erwartete_packzeit) ? r.erwartete_packzeit : s.erwartete_packzeit,
  };
}

// ── Eingabe und Ergebnis ─────────────────────────────────────────────────

export type Entscheidungsphase = 'vorlaeufig' | 'endgueltig';

export interface SendungsEingabe {
  phase: Entscheidungsphase;
  /** Gewicht in Gramm: Rechenwert (vorläufig) bzw. eingetragenes Gewicht (endgültig, R41). */
  gewicht_g: number;
  gewicht_quelle: 'rechenwert' | 'vorschlag' | 'eingetragen';
  /** Rechenwert unvollständig (Artikel ohne Gewicht, R104): Wahl nur unter Vorbehalt. */
  gewicht_vorbehalt: boolean;
  /** Außenmaß L ≥ B ≥ H in mm; null = unbekannt (Sonderverpackung, keine Verpackung). */
  aussen_mm: { l: number; b: number; h: number } | null;
  verpackung_nr: number | null;
  lieferland: string;
  zieltyp: Zieltyp;
  empfaengertyp: Empfaengertyp;
  warenwert_cents: number;
  /** Kalendertag und Uhrzeit (Europe/Berlin) des (erwarteten) Packzeitpunkts. */
  tag: string;
  uhrzeit: string;
}

export interface Kontext {
  /** Adapter, die nach R86 gesperrt sind (FEHLER/AUS). */
  gesperrteAdapter?: readonly string[];
  /** Heute schon für ein Konto gewählte Sendungen (Abholgebühr-Anteil, Stufe 1 Schwellenregel). */
  heuteJeKonto?: Readonly<Record<string, number>>;
}

export const AUSSCHLUSSGRUENDE = [
  'inaktiv', 'ungueltig', 'keine_abholung', 'masse_unbekannt', 'zu_schwer', 'zu_gross', 'kein_tarif', 'zieltyp', 'kein_tracking',
  'haftung', 'praeferenz', 'adapter_gesperrt', 'laufzeit',
] as const;
export type Ausschlussgrund = (typeof AUSSCHLUSSGRUENDE)[number];

export const AUSSCHLUSS_TEXT: Record<Ausschlussgrund, string> = {
  inaktiv: 'nicht aktiv',
  ungueltig: 'am Versandtag nicht gültig',
  keine_abholung: 'keine Abholung',
  masse_unbekannt: 'Außenmaß unbekannt',
  zu_schwer: 'zu schwer',
  zu_gross: 'zu groß',
  kein_tarif: 'kein gültiger Tarif',
  zieltyp: 'Ziel nicht belieferbar',
  kein_tracking: 'ohne Sendungsverfolgung',
  haftung: 'Haftung reicht nicht',
  praeferenz: 'durch Präferenz ausgeschlossen',
  adapter_gesperrt: 'Carrier-Anbindung gesperrt',
  laufzeit: 'zu späte Abholung',
};

export interface Kostenposten {
  art: 'porto' | 'zuschlag' | 'praeferenz' | 'abholgebuehr' | 'verzug';
  name: string;
  cents: number;
  quelle?: string | null;
  unsicher?: boolean;
}

export interface Kandidat {
  option_id: string;
  option_code: string;
  option_name: string;
  konto_code: string;
  zulaessig: boolean;
  ausschluss?: { grund: Ausschlussgrund; text: string };
  tarifklasse_id?: string;
  tarifklasse_name?: string;
  versandtag?: string;
  verzug_werktage?: number;
  porto_cents?: number;
  zuschlaege_cents?: number;
  praeferenz_cents?: number;
  abholgebuehr_cents?: number;
  verzug_cents?: number;
  summe_cents?: number;
  posten?: Kostenposten[];
  hinweise: string[];
}

export type Entscheidungsregel = 'guenstigste' | 'einzige_zulaessige' | 'keine_option';

export interface Versandwahl {
  option_id: string | null;
  option_code: string | null;
  option_name: string | null;
  tarifklasse_id: string | null;
  regel: Entscheidungsregel;
  /** Klartext „warum“, ohne Vertragspreise (R117: Differenzen ja, Preise nein). */
  grund: string;
  versandtag: string | null;
  porto_cents: number | null;
  zuschlaege_cents: number | null;
  praeferenz_cents: number | null;
  prozess_cents: number | null;
  summe_cents: number | null;
  kandidaten: Kandidat[];
}

// ── Datum ────────────────────────────────────────────────────────────────

const TAG = /^\d{4}-\d{2}-\d{2}$/;
function alsUtc(tag: string): Date {
  if (!TAG.test(tag)) throw new Error(`Tag als YYYY-MM-DD erwartet: ${tag}`);
  return new Date(`${tag}T00:00:00Z`);
}
const isoTag = (d: Date) => d.toISOString().slice(0, 10);
export function plusTage(tag: string, n: number): string { const d = alsUtc(tag); d.setUTCDate(d.getUTCDate() + n); return isoTag(d); }
/** ISO-Wochentag 1 = Montag … 7 = Sonntag. */
export function wochentag(tag: string): number { const w = alsUtc(tag).getUTCDay(); return w === 0 ? 7 : w; }
export function istWerktag(tag: string): boolean { return wochentag(tag) <= 5; }
/** Werktage (Mo–Fr) nach `von` bis einschließlich `bis`; 0, wenn `bis` ≤ `von`. Feiertage kennt Stufe 1 nicht. */
export function werktageNach(von: string, bis: string): number {
  let n = 0;
  for (let t = von; t < bis; ) { t = plusTage(t, 1); if (istWerktag(t)) n++; }
  return n;
}
const gilt = (von: string, bis: string | null, tag: string) => von <= tag && (bis === null || tag <= bis);

/**
 * Versandtag einer Option (R116): heute, wenn heute ein Abholtag des Kontos ist und die Uhrzeit vor dem Annahmeschluss
 * liegt, sonst der nächste Abholtag. Abholung auf Abruf braucht `abholung_vorlauf_werktage` Abholtage Vorlauf.
 */
export function versandtagFuer(option: Pick<Versandoption, 'annahmeschluss'>, konto: Pick<Versandkonto, 'abholtage' | 'abholung' | 'abholung_vorlauf_werktage'>, tag: string, uhrzeit: string): string {
  const tage = konto.abholtage.length ? konto.abholtage : [1, 2, 3, 4, 5];
  const istAbholtag = (t: string) => tage.includes(wochentag(t));
  const vorlauf = konto.abholung === 'auf_abruf' ? Math.max(0, konto.abholung_vorlauf_werktage) : 0;
  let t = tag;
  let rest = vorlauf;
  if (vorlauf === 0 && istAbholtag(t) && uhrzeit <= option.annahmeschluss) return t;
  if (vorlauf > 0 && uhrzeit > option.annahmeschluss) rest++;
  for (let i = 0; i < 60; i++) {
    t = plusTage(t, 1);
    if (!istAbholtag(t)) continue;
    if (rest <= 1) return t;
    rest--;
  }
  return t;
}

// ── Entscheidung ─────────────────────────────────────────────────────────

const euro = (c: number) => eur(c);

type Grenzen = { max_g: number | null; max_l_mm: number | null; max_b_mm: number | null; max_h_mm: number | null; max_lk_mm: number | null };
const mitMass = (g: Grenzen) => g.max_l_mm !== null || g.max_b_mm !== null || g.max_h_mm !== null || g.max_lk_mm !== null;

/**
 * Grenzen prüfen. Unbekanntes Außenmaß (Sonderverpackung): An der Option sind die Maße Plausibilitätsgrenzen (Paket
 * 120 × 60 × 60 cm) und werden dann nicht geprüft (Hinweis); an der Tarifklasse bestimmen sie den Preis (Kleinpaket),
 * dort schließt ein unbekanntes Maß die Klasse aus. So wird nie ein Maß geraten.
 */
function passtMass(g: Grenzen, gewicht: number, a: SendungsEingabe['aussen_mm'], unbekanntOk = false):
  'ok' | 'zu_schwer' | 'zu_gross' | 'masse_unbekannt' {
  if (g.max_g !== null && gewicht > g.max_g) return 'zu_schwer';
  if (!mitMass(g)) return 'ok';
  if (!a) return unbekanntOk ? 'ok' : 'masse_unbekannt';
  if ((g.max_l_mm !== null && a.l > g.max_l_mm) || (g.max_b_mm !== null && a.b > g.max_b_mm) || (g.max_h_mm !== null && a.h > g.max_h_mm)) return 'zu_gross';
  if (g.max_lk_mm !== null && a.l + a.h > g.max_lk_mm) return 'zu_gross';
  return 'ok';
}

function ausgeschlossen(k: Kandidat, grund: Ausschlussgrund, detail?: string): Kandidat {
  return { ...k, zulaessig: false, ausschluss: { grund, text: detail ? `${AUSSCHLUSS_TEXT[grund]} (${detail})` : AUSSCHLUSS_TEXT[grund] } };
}

/**
 * Die Wahl je Sendung (§ 2). Harte Filter in fester Reihenfolge (erster greifender Grund zählt), dann Kosten, dann die
 * günstigste zulässige Option; Gleichstand: früherer Versandtag, dann kleinerer Rang, dann Code.
 */
export function waehleVersand(e: SendungsEingabe, t: Tarifstand, regeln: Versandregeln, ktx: Kontext = {}): Versandwahl {
  const kandidaten: Kandidat[] = [];
  const gesperrt = new Set(ktx.gesperrteAdapter ?? []);
  const offen: Array<{ k: Kandidat; o: Versandoption; konto: Versandkonto; klasse: Tarifklasse }> = [];

  for (const o of [...t.optionen].sort((a, b) => a.rang - b.rang || a.code.localeCompare(b.code))) {
    const konto = t.konten.find((k) => k.id === o.konto_id);
    let k: Kandidat = { option_id: o.id, option_code: o.code, option_name: o.name, konto_code: konto?.code ?? '?', zulaessig: true, hinweise: [] };
    if (o.unsicher && o.hinweis) k.hinweise.push(o.hinweis);
    // 1. Gültigkeit
    if (!konto || !konto.aktiv || !o.aktiv) { kandidaten.push(ausgeschlossen(k, 'inaktiv', !konto ? 'Konto fehlt' : !konto.aktiv ? `Konto ${konto.name}` : undefined)); continue; }
    if (!gilt(konto.gueltig_von, konto.gueltig_bis, e.tag)) { kandidaten.push(ausgeschlossen(k, 'ungueltig', `Konto ${konto.name}`)); continue; }
    // 2. Abholung verfügbar (Vorgabe Thomas 18.10.2026, R115)
    if (o.einlieferung === 'abholung') {
      if (konto.abholung === 'keine') { kandidaten.push(ausgeschlossen(k, 'keine_abholung', 'Konto ohne Abholvereinbarung')); continue; }
      if (!o.abholung_bestaetigt) { kandidaten.push(ausgeschlossen(k, 'keine_abholung', 'Mitnahme bei der Abholung nicht bestätigt')); continue; }
    } else if (!regeln.selbsteinlieferung_erlaubt) {
      kandidaten.push(ausgeschlossen(k, 'keine_abholung', 'Selbsteinlieferung ist ausgeschaltet')); continue;
    }
    // 3. Maße und Gewicht: Grenzen der Option, dann erste passende Tarifklasse
    const om = passtMass(o, e.gewicht_g, e.aussen_mm, true);
    if (om !== 'ok') { kandidaten.push(ausgeschlossen(k, om, om === 'zu_schwer' ? `${e.gewicht_g} g > ${o.max_g} g` : undefined)); continue; }
    const gueltig = t.klassen.filter((c) => c.option_id === o.id && c.zone === e.lieferland && gilt(c.gueltig_von, c.gueltig_bis, e.tag)).sort((a, b) => a.rang - b.rang);
    if (!gueltig.length) { kandidaten.push(ausgeschlossen(k, 'kein_tarif', `kein Preis für ${e.lieferland} am ${e.tag}`)); continue; }
    const klasse = gueltig.find((c) => passtMass(c, e.gewicht_g, e.aussen_mm) === 'ok');
    if (!klasse) {
      const g = gueltig.map((c) => passtMass(c, e.gewicht_g, e.aussen_mm));
      kandidaten.push(ausgeschlossen(k, g.every((x) => x === 'zu_schwer') ? 'zu_schwer' : g.includes('masse_unbekannt') ? 'masse_unbekannt' : 'zu_gross'));
      continue;
    }
    // 4. Zieltyp
    if (!o.zieltypen.includes(e.zieltyp)) { kandidaten.push(ausgeschlossen(k, 'zieltyp', e.zieltyp === 'adresse' ? 'Hausadresse' : e.zieltyp === 'packstation' ? 'Packstation' : 'Postfiliale')); continue; }
    // 5. Tracking
    if (regeln.tracking_pflicht && !o.tracking) { kandidaten.push(ausgeschlossen(k, 'kein_tracking')); continue; }
    // 6. Haftung
    if (o.haftung_cents !== null && e.warenwert_cents > o.haftung_cents && e.warenwert_cents > regeln.selbstversicherung_bis_cents) {
      kandidaten.push(ausgeschlossen(k, 'haftung', `Warenwert ${euro(e.warenwert_cents)} > ${euro(o.haftung_cents)}`)); continue;
    }
    // 7. Präferenz als Ausschluss
    const prefs = t.praeferenzen.filter((p) => p.option_id === o.id && gilt(p.gueltig_von, p.gueltig_bis, e.tag) && p.bedingung === 'empfaengertyp' && p.wert === e.empfaengertyp);
    const raus = prefs.find((p) => p.wirkung === 'ausschluss');
    if (raus) { kandidaten.push(ausgeschlossen(k, 'praeferenz', raus.grund)); continue; }
    // 8. Adapter benutzbar (R86)
    if (gesperrt.has(konto.adapter)) { kandidaten.push(ausgeschlossen(k, 'adapter_gesperrt', konto.adapter)); continue; }

    if (e.gewicht_vorbehalt) k.hinweise.push('Gewicht unvollständig: Wahl unter Vorbehalt');
    if (!e.aussen_mm && mitMass(o)) k.hinweise.push('Außenmaß unbekannt: Maßgrenzen nicht geprüft');
    if (klasse.unsicher) k.hinweise.push(`Preis ${klasse.name} unsicher (${klasse.quelle ?? 'ohne Quelle'})`);
    k = { ...k, tarifklasse_id: klasse.id, tarifklasse_name: klasse.name };
    offen.push({ k, o, konto, klasse });
  }

  // Abholfenster: Versandtag je Option, Verzug gegenüber der frühesten (R116)
  const mitTag = offen.map((x) => ({ ...x, versandtag: versandtagFuer(x.o, x.konto, e.tag, e.uhrzeit) }));
  const fruehester = mitTag.reduce<string | null>((m, x) => (m === null || x.versandtag < m ? x.versandtag : m), null);
  const zulaessig: Kandidat[] = [];
  for (const x of mitTag) {
    const verzug = fruehester ? werktageNach(fruehester, x.versandtag) : 0;
    if (verzug > regeln.laufzeitverlust_max_werktage) {
      kandidaten.push(ausgeschlossen(x.k, 'laufzeit', `Abholung erst am ${x.versandtag}, ${verzug} Werktage nach der frühesten`));
      continue;
    }
    // Kosten (R114: Grenzkosten; Grundgebühr nie)
    const posten: Kostenposten[] = [{ art: 'porto', name: x.klasse.name, cents: x.klasse.preis_cents, quelle: x.klasse.quelle, unsicher: x.klasse.unsicher }];
    const zus = t.zuschlaege.filter((z) => z.konto_id === x.konto.id && (z.option_id === null || z.option_id === x.o.id) && gilt(z.gueltig_von, z.gueltig_bis, x.versandtag)
      && (z.bedingung === 'immer' || z.bedingung === 'zeitraum' || (z.bedingung === 'zieltyp_packstation' && e.zieltyp === 'packstation') || (z.bedingung === 'zieltyp_postfiliale' && e.zieltyp === 'postfiliale')));
    for (const z of zus) {
      const c = z.art === 'fix' ? (z.betrag_cents ?? 0) : rundeCents((x.klasse.preis_cents * (z.basispunkte ?? 0)) / 10_000);
      posten.push({ art: 'zuschlag', name: z.art === 'prozent' ? `${z.name} ${centsAlsText(z.basispunkte ?? 0)} %` : z.name, cents: c, quelle: z.quelle, unsicher: z.unsicher });
    }
    for (const p of prefs0(t, x.o.id, e)) posten.push({ art: 'praeferenz', name: p.grund, cents: p.betrag_cents ?? 0, quelle: p.quelle });
    const n = (ktx.heuteJeKonto?.[x.konto.id] ?? 0) + 1;
    const abhol = x.o.abholgebuehr_tag_cents > 0 ? Math.round(x.o.abholgebuehr_tag_cents / n) : 0;
    if (abhol) posten.push({ art: 'abholgebuehr', name: `Abholgebühr ${euro(x.o.abholgebuehr_tag_cents)} / ${n} Paket(e)`, cents: abhol });
    const verzugCents = verzug * regeln.verzug_cents_je_werktag;
    if (verzug) posten.push({ art: 'verzug', name: `${verzug} Werktag(e) später × ${euro(regeln.verzug_cents_je_werktag)}`, cents: verzugCents });
    const summe = (a: Kostenposten['art']) => posten.filter((p) => p.art === a).reduce((s, p) => s + p.cents, 0);
    const k: Kandidat = {
      ...x.k, versandtag: x.versandtag, verzug_werktage: verzug,
      porto_cents: summe('porto'), zuschlaege_cents: summe('zuschlag'), praeferenz_cents: summe('praeferenz'), abholgebuehr_cents: summe('abholgebuehr'), verzug_cents: summe('verzug'),
      summe_cents: posten.reduce((s, p) => s + p.cents, 0), posten,
    };
    kandidaten.push(k);
    zulaessig.push(k);
  }

  const rang = new Map(t.optionen.map((o) => [o.id, o.rang]));
  zulaessig.sort((a, b) => a.summe_cents! - b.summe_cents! || a.versandtag!.localeCompare(b.versandtag!) || (rang.get(a.option_id)! - rang.get(b.option_id)!) || a.option_code.localeCompare(b.option_code));
  const sieger = zulaessig[0] ?? null;
  const regel: Entscheidungsregel = !sieger ? 'keine_option' : zulaessig.length === 1 ? 'einzige_zulaessige' : 'guenstigste';
  return {
    option_id: sieger?.option_id ?? null, option_code: sieger?.option_code ?? null, option_name: sieger?.option_name ?? null,
    tarifklasse_id: sieger?.tarifklasse_id ?? null, regel, grund: grundText(sieger, zulaessig, kandidaten, e),
    versandtag: sieger?.versandtag ?? null,
    porto_cents: sieger?.porto_cents ?? null, zuschlaege_cents: sieger?.zuschlaege_cents ?? null, praeferenz_cents: sieger?.praeferenz_cents ?? null,
    prozess_cents: sieger ? (sieger.abholgebuehr_cents ?? 0) + (sieger.verzug_cents ?? 0) : null,
    summe_cents: sieger?.summe_cents ?? null,
    kandidaten,
  };
}

function prefs0(t: Tarifstand, optionId: string, e: SendungsEingabe): Praeferenz[] {
  return t.praeferenzen.filter((p) => p.option_id === optionId && p.wirkung === 'aufschlag' && p.bedingung === 'empfaengertyp' && p.wert === e.empfaengertyp && gilt(p.gueltig_von, p.gueltig_bis, e.tag));
}

function abholText(versandtag: string, e: SendungsEingabe): string {
  if (versandtag === e.tag) return 'erreicht die Abholung heute';
  return `Abholung am ${versandtag.slice(8, 10)}.${versandtag.slice(5, 7)}.${versandtag.slice(0, 4)}`;
}

/** Klartext ohne Vertragspreise: nur Differenzen zwischen Optionen (R117). */
function grundText(sieger: Kandidat | null, zulaessig: Kandidat[], alle: Kandidat[], e: SendungsEingabe): string {
  const aus = alle.filter((k) => !k.zulaessig).map((k) => `${k.option_name} ausgeschlossen: ${k.ausschluss!.text}.`);
  if (!sieger) return ['Keine zulässige Versandoption.', ...aus].join(' ');
  const teile: string[] = [];
  if (zulaessig.length === 1) teile.push(`${sieger.option_name}: einzige zulässige Option, ${abholText(sieger.versandtag!, e)}.`);
  else {
    const zweite = zulaessig[1];
    const diff = zweite.summe_cents! - sieger.summe_cents!;
    teile.push(`${sieger.option_name}: ${diff > 0 ? `${euro(diff)} günstiger als ${zweite.option_name}` : `gleich teuer wie ${zweite.option_name}, Vorrang nach Abholtag/Rang`}, ${abholText(sieger.versandtag!, e)}.`);
  }
  const pref = (sieger.posten ?? []).filter((p) => p.art === 'praeferenz');
  for (const p of pref) teile.push(`Präferenz: ${p.name} (${p.cents >= 0 ? '+' : ''}${euro(p.cents)}).`);
  if (e.gewicht_vorbehalt) teile.push('Gewicht unvollständig, Wahl unter Vorbehalt.');
  return [...teile, ...aus].join(' ');
}

// ── Sicht der Rolle lager (R117) ─────────────────────────────────────────

export interface KandidatLager {
  option_code: string; option_name: string; zulaessig: boolean; ausschluss?: { grund: Ausschlussgrund; text: string };
  versandtag?: string;
  /** Mehrkosten gegenüber der gewählten Option in Cent (0 = gewählt bzw. gleich teuer). */
  differenz_cents?: number;
  hinweise: string[];
}

/** Kandidaten ohne Vertragspreise: nur Differenz zur gewählten Option. */
export function kandidatenFuerLager(kandidaten: readonly Kandidat[], gewaehltCode: string | null): KandidatLager[] {
  const basis = kandidaten.find((k) => k.option_code === gewaehltCode && k.zulaessig)?.summe_cents ?? null;
  return kandidaten.map((k) => ({
    option_code: k.option_code, option_name: k.option_name, zulaessig: k.zulaessig, ausschluss: k.ausschluss, versandtag: k.versandtag,
    differenz_cents: k.zulaessig && basis !== null && k.summe_cents !== undefined ? k.summe_cents - basis : undefined,
    hinweise: k.hinweise.filter((h) => !/Preis .* unsicher/.test(h)),
  }));
}

// ── Spitzentag ───────────────────────────────────────────────────────────

/** DHL: Bedarfsabholung bis 10 Pakete, darüber Einmalabholung (carrier-tarife-2026.md § 3). */
export const DHL_BEDARFSABHOLUNG_MAX = 10;

export interface SpitzentagVorschlag { vorschlagen: boolean; art: 'zusatz' | 'einmal' | null; text: string | null }

/**
 * Zusatzabholung an Spitzentagen (§ 5.7): ab `abholung_zusatz_ab_paketen` schlägt StoreOS eine Abholung vor. Stufe 1
 * beauftragt nie selbst; steht die Regel auf „beauftragen“, sagt der Text, dass das mit der Pickup-API kommt.
 */
export function spitzentagVorschlag(paketeHeute: number, regeln: Versandregeln): SpitzentagVorschlag {
  if (paketeHeute < regeln.abholung_zusatz_ab_paketen) return { vorschlagen: false, art: null, text: null };
  const art = paketeHeute > DHL_BEDARFSABHOLUNG_MAX ? 'einmal' : 'zusatz';
  const was = art === 'einmal' ? 'Einmalabholung (mehr als 10 Pakete)' : 'Zusatzabholung';
  const zusatz = regeln.spitzentag_verhalten === 'beauftragen' ? ' Automatisches Beauftragen folgt mit der Pickup-Anbindung; bis dahin bitte manuell beauftragen.' : '';
  return { vorschlagen: true, art, text: `${paketeHeute} Pakete heute: ${was} bei DHL beauftragen und als Abholauftrag erfassen.${zusatz}` };
}

// ── Abholauftrag (R124) ──────────────────────────────────────────────────

export const ABHOLUNG_STATUS = ['geplant', 'beauftragt', 'bestaetigt', 'abgeholt', 'storniert', 'fehlgeschlagen'] as const;
export type AbholungStatus = (typeof ABHOLUNG_STATUS)[number];
export const ABHOLUNG_ARTEN = ['bedarf', 'einmal', 'zusatz'] as const;
export type AbholungArt = (typeof ABHOLUNG_ARTEN)[number];

/** Statusautomat: geplant → beauftragt → bestätigt → abgeholt; offen → storniert (Grund Pflicht); beauftragt → fehlgeschlagen. */
export const ABHOLUNG_UEBERGAENGE: Record<AbholungStatus, readonly AbholungStatus[]> = {
  geplant: ['beauftragt', 'storniert'],
  beauftragt: ['bestaetigt', 'storniert', 'fehlgeschlagen'],
  bestaetigt: ['abgeholt', 'storniert'],
  abgeholt: [],
  storniert: [],
  fehlgeschlagen: ['beauftragt', 'storniert'],
};

export function abholungUebergangErlaubt(von: AbholungStatus, nach: AbholungStatus): boolean {
  return ABHOLUNG_UEBERGAENGE[von].includes(nach);
}

export const ABHOLUNG_STATUS_TEXT: Record<AbholungStatus, string> = {
  geplant: 'geplant', beauftragt: 'beauftragt', bestaetigt: 'bestätigt', abgeholt: 'abgeholt', storniert: 'storniert', fehlgeschlagen: 'fehlgeschlagen',
};

export interface Abholauftrag {
  id: string; brand_id: string; konto_id: string; konto_name: string; tag: string; fenster_von: string; fenster_bis: string;
  art: AbholungArt; anzahl_pakete: number; gewicht_g: number | null; status: AbholungStatus;
  ausloeser: 'tagesabschluss' | 'packstation' | 'manuell' | 'optimierer';
  carrier_referenz: string | null; bemerkung: string | null; storno_grund: string | null; fehler: string | null;
  beauftragt_at: string | null; bestaetigt_at: string | null; abgeholt_at: string | null; storniert_at: string | null;
  benutzer: string | null; created_at: string;
}

/** Schattenentscheidung, wie sie Auftragsdetail und Packstation zeigen (Rolle lager: ohne Beträge, Kandidaten als Differenz). */
export interface VersandEntscheidungSicht {
  id: string;
  shipment_id: string;
  phase: Entscheidungsphase;
  schalter: VersandroutingModus;
  wirksam: boolean;
  option_code: string | null;
  option_name: string | null;
  regel: Entscheidungsregel;
  grund: string;
  versandtag: string | null;
  /** Was der heutige Weg (versandproduktFuer + DHL-Adapter) macht. */
  heute: { carrier: string | null; versandprodukt: string | null; option_code: string | null; text: string };
  abweichung: boolean;
  eingabe: Omit<SendungsEingabe, 'warenwert_cents'> & { warenwert_cents?: number };
  summe_cents?: number | null;
  porto_cents?: number | null;
  zuschlaege_cents?: number | null;
  praeferenz_cents?: number | null;
  prozess_cents?: number | null;
  kandidaten: Array<Kandidat | KandidatLager>;
  created_at: string;
}
